import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SendIcon from "@mui/icons-material/Send";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Alert,
  Box,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogProps,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import {
  Autocomplete,
  Button,
  globalMessageHandler,
  PageHeader,
  Spacer,
  Toolbar,
} from "common";
import Dialog from "components/DraggableDialog/DraggableDialog";
import {
  BulkEmailPreviewResponse,
  previewBulkEmails,
  previewBulkEmailsByEvent,
  queueBulkEmails,
  queueBulkEmailsByEvent,
} from "features/bulk-emails-sent/bulkEmailSentApi";
import { getEmailTemplateService } from "features/email-templates/emailTemplateApi";
import I18n from "i18n";
import moment from "moment";
import { useCallback, useEffect, useState } from "react";

export interface BulkEmailModalProps extends Omit<DialogProps, "onClose"> {
  bookingIds?: number[];
  eventIds?: number[];
  onClose?: () => void;
  afterSend?: () => void;
}

export default function BulkEmailModal({
  bookingIds,
  eventIds,
  onClose,
  afterSend,
  ...props
}: BulkEmailModalProps) {
  const [templateId, setTemplateId] = useState<number | null>(null);
  const [preview, setPreview] = useState<BulkEmailPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailTemplateService = getEmailTemplateService();

  const isEventMode = !!eventIds && eventIds.length > 0;
  const isBookingMode = !!bookingIds && bookingIds.length > 0;

  const loadPreview = useCallback(async () => {
    if (!templateId) {
      setPreview(null);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let response: BulkEmailPreviewResponse;
      if (isEventMode) {
        response = await previewBulkEmailsByEvent(eventIds!, templateId);
      } else if (isBookingMode) {
        response = await previewBulkEmails(bookingIds!, templateId);
      } else {
        throw new Error("No booking or event IDs provided");
      }
      setPreview(response);
    } catch (err: any) {
      setError(err.message || "Hiba történt az előnézet betöltésekor");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, [templateId, bookingIds, eventIds, isEventMode, isBookingMode]);

  useEffect(() => {
    loadPreview();
  }, [loadPreview]);

  const handleSend = async () => {
    if (!templateId || !preview) return;

    setSending(true);
    try {
      if (isEventMode) {
        await queueBulkEmailsByEvent(eventIds!, templateId);
      } else if (isBookingMode) {
        await queueBulkEmails(bookingIds!, templateId);
      }

      globalMessageHandler.snack({
        severity: "success",
        message: I18n.t("BulkEmail.successMessage"),
      });

      afterSend?.();
      onClose?.();
    } catch (err: any) {
      globalMessageHandler.snack({
        severity: "error",
        message: err.message || I18n.t("BulkEmail.errorMessage"),
      });
    } finally {
      setSending(false);
    }
  };

  const title = isEventMode
    ? I18n.t("BulkEmail.titleByEvent")
    : I18n.t("BulkEmail.titleByBooking");

  const subtitle = isEventMode
    ? `${eventIds?.length} ${I18n.t("BulkEmail.selectedEvents")}`
    : `${bookingIds?.length} ${I18n.t("BulkEmail.selectedBookings")}`;

  // Calculate limit status
  const limitInfo = preview?.limit_info;
  const isLimitExceeded =
    limitInfo && preview?.summary.eligible_bookings > limitInfo.remaining;
  const limitPercentage = limitInfo
    ? ((limitInfo.sent_today + limitInfo.pending_in_queue) /
        limitInfo.daily_limit) *
      100
    : 0;

  return (
    <Dialog maxWidth="md" fullWidth {...props} onClose={onClose}>
      <PageHeader
        avatar={<SendIcon />}
        title={title}
        subtitle={subtitle}
        onClose={onClose}
        loading={loading}
      />
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Autocomplete
            label={I18n.t("BulkEmail.templateLabel")}
            loadItems={emailTemplateService.loadAutocomplete}
            value={templateId}
            onChange={(evt: any) => setTemplateId(evt.target.value)}
            disableClearable={false}
            fullWidth
          />
        </Box>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {preview && !loading && (
          <>
            <Alert
              severity={
                preview.summary.eligible_bookings > 0 ? "info" : "warning"
              }
              sx={{ mb: 2 }}
            >
              <Typography variant="body2">
                <strong>{I18n.t("BulkEmail.templateName")}:</strong>{" "}
                {preview.template_name}
              </Typography>
              <Typography variant="body2">
                <strong>{I18n.t("BulkEmail.totalBookings")}:</strong>{" "}
                {preview.summary.total_bookings}
              </Typography>
              <Typography variant="body2">
                <strong>{I18n.t("BulkEmail.eligibleBookings")}:</strong>{" "}
                {preview.summary.eligible_bookings}
              </Typography>
              <Typography variant="body2">
                <strong>{I18n.t("BulkEmail.skippedBookings")}:</strong>{" "}
                {preview.summary.skipped_bookings}
              </Typography>
            </Alert>

            {preview.events && preview.events.length > 0 && (
              <>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {I18n.t("BulkEmail.eventBreakdown")}:
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{I18n.t("BulkEmail.eventProgram")}</TableCell>
                      <TableCell>
                        {I18n.t("BulkEmail.eventStartTime")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("BulkEmail.eventTotal")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("BulkEmail.eventEligible")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("BulkEmail.eventNoEmail")}
                      </TableCell>
                      <TableCell align="right">
                        {I18n.t("BulkEmail.eventAlreadySent")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.events.map(event => (
                      <TableRow key={event.event_id}>
                        <TableCell>{event.program_name}</TableCell>
                        <TableCell>
                          {moment(event.start_time).format("L LT")}
                        </TableCell>
                        <TableCell align="right">{event.total}</TableCell>
                        <TableCell align="right">{event.eligible}</TableCell>
                        <TableCell align="right">{event.no_email}</TableCell>
                        <TableCell align="right">
                          {event.already_sent}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </>
            )}

            {preview.summary.skipped_bookings > 0 && (
              <Toolbar sx={{ mt: 2 }}>
                <WarningIcon color="warning" />
                <Spacer w={1} />
                <Typography variant="body2" color="text.secondary">
                  {I18n.t("BulkEmail.skippedInfo")}
                </Typography>
              </Toolbar>
            )}

            {limitInfo && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  bgcolor: "background.default",
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {I18n.t("BulkEmail.dailyLimitTitle")}
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Box sx={{ flexGrow: 1, mr: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(limitPercentage, 100)}
                      color={
                        isLimitExceeded
                          ? "error"
                          : limitInfo.queue_will_reached
                            ? "warning"
                            : "primary"
                      }
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {limitInfo.sent_today + limitInfo.pending_in_queue} /{" "}
                    {limitInfo.daily_limit}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <Typography variant="caption" color="text.secondary">
                    {I18n.t("BulkEmail.sentToday")}: {limitInfo.sent_today}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {I18n.t("BulkEmail.pendingInQueue")}:{" "}
                    {limitInfo.pending_in_queue}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {I18n.t("BulkEmail.remaining")}: {limitInfo.remaining}
                  </Typography>
                </Box>
                {isLimitExceeded && (
                  <Toolbar sx={{ mt: 1, p: 0 }}>
                    <ErrorOutlineIcon color="error" fontSize="small" />
                    <Spacer w={1} />
                    <Typography variant="body2" color="error">
                      {I18n.t("BulkEmail.limitExceeded", {
                        eligible: preview.summary.eligible_bookings,
                        remaining: limitInfo.remaining,
                      })}
                    </Typography>
                  </Toolbar>
                )}
                {!isLimitExceeded && limitInfo.queue_will_reached && (
                  <Toolbar sx={{ mt: 1, p: 0 }}>
                    <WarningIcon color="warning" fontSize="small" />
                    <Spacer w={1} />
                    <Typography variant="body2" color="warning.main">
                      {I18n.t("BulkEmail.queueNearlyFull")}
                    </Typography>
                  </Toolbar>
                )}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button title={I18n.t("App.cancel")} onClick={onClose} variant="text" />
        <Button
          title={I18n.t("BulkEmail.sendButton")}
          onClick={handleSend}
          loading={sending}
          disabled={
            !templateId ||
            !preview ||
            preview.summary.eligible_bookings === 0 ||
            isLimitExceeded
          }
          startIcon={<SendIcon />}
        />
      </DialogActions>
    </Dialog>
  );
}
